import type { QuestionnaireGroupDto, QuestionnaireQuestionDto } from "Infrastructure/ApiZfp/Schema";
import type { GoalOverview } from "Infrastructure/ApiZfp/Schema/goalOverview";
import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import type { InvestmentPlan } from "./types";

/**
 * "print" is the same questionnaire rendered into the PDF template. Both modes share every component;
 * the mode only switches off what makes no sense on paper (hover, tooltips, required markers).
 */
export type QuestionnaireMode = "form" | "print";

type QuestionnaireContextValue = {
  goalsData: GoalOverview[];
  allQuestions: QuestionnaireQuestionDto[];
  groups: QuestionnaireGroupDto[];
  investmentPlans: InvestmentPlan[];
  organizedBy: string;
  isReadOnly: boolean;
  mode: QuestionnaireMode;
};

type ProviderProps = Omit<QuestionnaireContextValue, "mode"> & { mode?: QuestionnaireMode; children: ReactNode };

export const QuestionnaireContext = createContext<QuestionnaireContextValue | null>(null);

export const QuestionnaireProvider = ({
  goalsData,
  allQuestions,
  groups,
  children,
  investmentPlans,
  organizedBy,
  isReadOnly,
  mode = "form",
}: ProviderProps) => {
  const value = useMemo(
    () => ({ goalsData, investmentPlans, allQuestions, groups, organizedBy, isReadOnly, mode }),
    [goalsData, investmentPlans, allQuestions, groups, organizedBy, isReadOnly, mode],
  );

  return <QuestionnaireContext.Provider value={value}>{children}</QuestionnaireContext.Provider>;
};

export const useQuestionnaireContext = (): QuestionnaireContextValue => {
  const ctx = useContext(QuestionnaireContext);
  if (ctx === null) throw new Error("useQuestionnaireContext must be used within QuestionnaireProvider");
  return ctx;
};

export const useIsPrintMode = (): boolean => useQuestionnaireContext().mode === "print";
